import clsx from 'clsx';
import * as React from 'react';
import { useSetRecoilState } from 'recoil';

import * as api from '@gitbook/api-client';
import { UserTheme } from '@gitbook/app-database';
import { type TableCardsView, type TableDefinitions, table } from '@gitbook/doc-core';
import { useHiveClient } from '@gitbook/hive-react';
import {
    createPalette,
    PaletteCommandType,
    useConfigurationTheme,
    usePaletteHandle,
} from '@gitbook/react-uikit';
import type { EditorContainer } from '@gitbook/slate-react';
import { Icon } from '@gitbook/spine-icons';
import { Button } from '@gitbook/spine-react';
import { useMergedRefs } from '@gitbook/util-react-hooks';

import { shortcuts } from '../../../../../Keyboard';
import { userThemeAtom } from '../../../../../state';
import {
    createInputContentRefPalette,
    useContentReferenceContext,
} from '../../../../ContentReferences';
import type { DraggableState } from '../../../../DragAndDrop';
import { useInsertFiles } from '../../../../InsertFilesDialog';
import type { TableRecordKV } from '../../types';
import cssStyles from './Cards.module.css';

/**
 * Button to control the card.
 */
export function TableRecordControlButton(props: {
    /** Is this row editable ? */
    readOnly: boolean;
    /** Key of the parent Table block */
    nodeKey: string;
    /** Editor instance */
    editor: EditorContainer;
    /** Record to render in this row */
    record: TableRecordKV;
    /** View configured in the table */
    view: TableCardsView;
    /** If true, the button will be visible, independantly from the hovering state */
    visible: boolean;
    /** Callback to expand the record as a modal */
    onExpand: () => void;
    /** Dragging state and event listeners to initiate dragging */
    draggableState: DraggableState;
    /** Definitions in the table */
    definition: TableDefinitions;
}): React.ReactElement {
    const {
        nodeKey,
        editor,
        record,
        view,
        visible,
        onExpand,
        draggableState,
        definition,
        readOnly,
    } = props;
    const { setNodeRef, isDragging, listeners } = draggableState;
    const { color: currentTheme } = useConfigurationTheme();
    const paletteButtonRef = React.useRef(null);
    const buttonRef = useMergedRefs(paletteButtonRef, setNodeRef);
    const paletteHandle = usePaletteHandle(paletteButtonRef);
    const contentReferenceContext = useContentReferenceContext();
    const hiveClient = useHiveClient();
    const coverRef = table.cards.getRecordCover({
        coverDefinition: view.coverDefinition,
        coverDefinitionDark: view.coverDefinitionDark,
        definition,
        record: record.value,
        currentTheme,
    });
    const objectFit = table.cards.getRecordCoverObjectFit({
        coverDefinition: view.coverDefinition,
        coverDefinitionDark: view.coverDefinitionDark,
        definition,
        record: record.value,
        currentTheme,
    });
    const insertFiles = useInsertFiles();
    const setUserTheme = useSetRecoilState(userThemeAtom);
    const hasCover = Boolean(coverRef);
    const hasTarget = view.targetDefinition ? !!record.value.values[view.targetDefinition] : false;

    const replaceTarget = () => {
        return createInputContentRefPalette(
            (targetRef) => {
                editor.change((change) => {
                    table.cards.setCardTargetForRecord(change, nodeKey, record.key, targetRef);
                });
            },
            contentReferenceContext,
            hiveClient,
            {
                accept: ['page', 'file', 'space', 'collection', 'anchor', 'url'],
            }
        );
    };

    const hasDarkMode = view.coverDefinitionDark
        ? Boolean(record.value.values[view.coverDefinitionDark])
        : false;

    const setObjectFit = (fit: api.CardsImageObjectFit) => {
        editor.change((change) => {
            table.cards.setCardCoverObjectFitForRecord(
                change,
                nodeKey,
                record.key,
                fit,
                currentTheme
            );
        });
    };

    const replaceCover = async (title: string, mode?: 'light' | 'dark' | 'both') => {
        const [file] = await insertFiles.prompt({
            title,
            accept: 'images',
            acceptURL: true,
        });

        if (!file) {
            return;
        }

        editor.change((change) => {
            table.cards.setCardCoverForRecord(change, nodeKey, record.key, file, mode);
        });
    };

    const onPress = (event: React.MouseEvent<HTMLButtonElement>) => {
        event.preventDefault();
        event.stopPropagation();

        paletteHandle.open(
            createPalette({
                groups: [
                    {
                        key: 'actions',
                        commands: [
                            {
                                key: 'actions.target',
                                icon: Icon.AddLink,
                                ...(hasTarget
                                    ? {
                                          title: 'Target link',
                                          descendant: () => {
                                              return createPalette({
                                                  groups: [
                                                      {
                                                          key: 'target',
                                                          commands: [
                                                              {
                                                                  key: 'target.set',
                                                                  title: 'Replace target',
                                                                  icon: Icon.Replace,
                                                                  trigger: () => {
                                                                      return replaceTarget();
                                                                  },
                                                              },
                                                              {
                                                                  key: 'target.remove',
                                                                  title: 'Remove target',
                                                                  icon: Icon.Trashcan,
                                                                  trigger: () => {
                                                                      editor.change((change) => {
                                                                          table.cards.removeCardTargetForRecord(
                                                                              change,
                                                                              nodeKey,
                                                                              record.key
                                                                          );
                                                                      });
                                                                  },
                                                              },
                                                          ],
                                                      },
                                                  ],
                                              });
                                          },
                                      }
                                    : {
                                          title: 'Add target link',
                                          trigger: () => {
                                              return replaceTarget();
                                          },
                                      }),
                            },
                            {
                                key: 'actions.cover',
                                icon: Icon.Image,
                                ...(hasCover
                                    ? {
                                          title: 'Cover',
                                          descendant: () => {
                                              return cardCoverPalette({
                                                  hasDarkMode,
                                                  replaceCover,
                                                  removeCover: () => {
                                                      editor.change((change) => {
                                                          table.cards.removeCardCoverForRecord(
                                                              change,
                                                              nodeKey,
                                                              record.key
                                                          );
                                                      });
                                                  },
                                                  objectFit,
                                                  setObjectFit,
                                              });
                                          },
                                      }
                                    : {
                                          title: 'Add cover',
                                          trigger: () => {
                                              replaceCover('Add a cover image', 'light');
                                          },
                                      }),
                            },
                            {
                                key: 'actions.delete',
                                title: 'Delete',
                                icon: Icon.Trashcan,
                                trigger: () => {
                                    editor.change((change) => {
                                        table.removeRecord(change, nodeKey, record.key);
                                    });
                                },
                            },
                        ],
                    },
                ],
            })
        );
    };

    return (
        <div
            className={clsx(
                'flex items-center',
                cssStyles.controlButtons,
                isDragging && 'opacity-50'
            )}
            style={paletteHandle.opened || visible ? { opacity: 1 } : undefined}
        >
            <div className="flex gap-1.5">
                {!readOnly ? (
                    <Button
                        ref={buttonRef}
                        kind="secondary"
                        size="small"
                        leadingIcon={Icon.GrabberVertical}
                        iconOnly
                        label="Drag card"
                        tooltip={{
                            label: 'Click for card options',
                            description: 'Drag to move',
                            shortcut: shortcuts.EDITOR_MODIFIER_PALETTE,
                            disabled: isDragging,
                        }}
                        onClick={onPress}
                        className={isDragging ? 'cursor-grabbing' : 'cursor-grab'}
                        {...listeners}
                    />
                ) : null}
                {hasCover && hasDarkMode && (
                    <Button
                        kind="secondary"
                        size="small"
                        iconOnly
                        leadingIcon={currentTheme === 'dark' ? Icon.ThemeLight : Icon.ThemeDark}
                        label={`Cover has a ${
                            currentTheme === 'light' ? 'dark' : 'light'
                        } mode alternative.\nToggle theme to see it.`}
                        onPress={() => {
                            setUserTheme(
                                currentTheme === 'light' ? UserTheme.Dark : UserTheme.Light
                            );
                        }}
                        onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                        }}
                    />
                )}
            </div>
            {!readOnly ? (
                <div className="ml-auto flex">
                    <Button
                        kind="secondary"
                        size="small"
                        iconOnly
                        leadingIcon={Icon.Pencil}
                        onPress={onExpand}
                        label="Edit card"
                    />
                </div>
            ) : null}
        </div>
    );
}

function cardCoverPalette({
    hasDarkMode,
    replaceCover,
    removeCover,
    objectFit,
    setObjectFit,
}: {
    hasDarkMode: boolean;
    replaceCover: (title: string, mode?: 'light' | 'dark' | 'both') => void;
    removeCover: () => void;
    objectFit: api.CardsImageObjectFit;
    setObjectFit: (fit: api.CardsImageObjectFit) => void;
}) {
    return createPalette({
        groups: [
            {
                key: 'cover',
                commands: [
                    {
                        key: 'cover.edit',
                        title: 'Edit',
                        icon: Icon.Pencil,
                        descendant: () => {
                            return createPalette({
                                groups: [
                                    {
                                        key: 'cover.edit',
                                        commands: [
                                            {
                                                key: 'cover.edit.replace',
                                                title: 'Replace cover',
                                                subtitle: 'For both light and dark modes',
                                                icon: Icon.Replace,
                                                trigger: () => {
                                                    replaceCover('Replace cover', 'both');
                                                },
                                            },
                                        ],
                                    },
                                    {
                                        key: 'cover.edit.theme',
                                        commands: hasDarkMode
                                            ? [
                                                  {
                                                      key: 'cover.edit.theme.light',
                                                      title: 'For light mode',
                                                      icon: Icon.ThemeLight,
                                                      trigger: () => {
                                                          replaceCover(
                                                              'Replace cover for Light mode',
                                                              'light'
                                                          );
                                                      },
                                                  },
                                                  {
                                                      key: 'cover.edit.theme.dark',
                                                      title: 'For dark mode',
                                                      icon: Icon.ThemeDark,
                                                      trigger: () => {
                                                          replaceCover(
                                                              'Replace cover for dark mode',
                                                              'dark'
                                                          );
                                                      },
                                                  },
                                              ]
                                            : [
                                                  {
                                                      key: 'cover.edit.theme.add_dark',
                                                      title: 'Add cover for dark mode',
                                                      icon: Icon.ThemeDark,
                                                      trigger: () => {
                                                          replaceCover(
                                                              'Add cover for dark mode',
                                                              'dark'
                                                          );
                                                      },
                                                  },
                                              ],
                                    },
                                ],
                            });
                        },
                    },
                    {
                        key: 'cover.fit',
                        title: 'Image fit',
                        icon: Icon.Image,
                        descendant: () => {
                            return createPalette({
                                groups: [
                                    {
                                        key: 'cover.fit.options',
                                        commands: [
                                            {
                                                key: 'cover.fit.cover',
                                                title: 'Cover available space',
                                                subtitle: 'Crops the image if necessary',
                                                type: PaletteCommandType.RADIO,
                                                selected:
                                                    objectFit === api.CardsImageObjectFit.Cover,
                                                trigger: () =>
                                                    setObjectFit(api.CardsImageObjectFit.Cover),
                                            },
                                            {
                                                key: 'cover.fit.fill',
                                                title: 'Fill available space',
                                                subtitle: 'Stretches the image if necessary',
                                                type: PaletteCommandType.RADIO,
                                                selected:
                                                    objectFit === api.CardsImageObjectFit.Fill,
                                                trigger: () =>
                                                    setObjectFit(api.CardsImageObjectFit.Fill),
                                            },
                                            {
                                                key: 'cover.fit.contain',
                                                title: 'Contain within space',
                                                subtitle: "Shows empty space if image doesn't fit",
                                                type: PaletteCommandType.RADIO,
                                                selected:
                                                    objectFit === api.CardsImageObjectFit.Contain,
                                                trigger: () =>
                                                    setObjectFit(api.CardsImageObjectFit.Contain),
                                            },
                                        ],
                                    },
                                ],
                            });
                        },
                    },
                    {
                        key: 'cover.remove',
                        title: 'Remove',
                        icon: Icon.Trashcan,
                        trigger: () => {
                            removeCover();
                        },
                    },
                ],
            },
        ],
    });
}
